import { useRef } from 'react';
import {
  X, Printer, CheckCircle2, AlertCircle, ShieldAlert,
  Droplets, ArrowDown, Wind, Beaker, Filter, Waves,
  Zap, Layers, BarChart3, FlaskConical, TestTube,
  CircleDot, Cylinder, Shield, Sun, Eye, BookOpen,
} from 'lucide-react';
import type { ValidationSection } from './EquipmentAdminValidation';

const iconMap: Record<string, React.ElementType> = {
  Droplets, ArrowDown, Wind, Beaker, Filter, Waves, Zap, Layers,
  BarChart3, FlaskConical, TestTube, CircleDot, Cylinder, Shield, Sun, Eye, BookOpen,
};

interface ValidationPrintReportProps {
  isOpen: boolean;
  onClose: () => void;
  equipmentName: string;
  sampleName?: string;
  customerName?: string;
  /** Site / location name */
  siteName?: string;
  date: string;
  /** Unique document reference — e.g. RB-DAF-EA-2026-4821 */
  reportNumber?: string;
  sections: ValidationSection[];
  passCount: number;
  warnCount: number;
  failCount: number;
  rawParams?: Record<string, number | string>;
  /** Regulatory body name — if set, a statutory compliance panel is shown in the PDF */
  regulatoryBody?: string;
  /** Legislation acts list for the regulatory body */
  regulatoryLegislation?: string[];
  /** Name of the engineer / user who prepared this report */
  preparedBy?: string;
  /** Engineer reference / user ID for the document footer */
  engineerRef?: string;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'pass') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> PASS
      </span>
    );
  }
  if (status === 'warn') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
        <AlertCircle className="w-3 h-3" /> WARN
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200">
      <ShieldAlert className="w-3 h-3" /> FAIL
    </span>
  );
}

export default function ValidationPrintReport({
  isOpen,
  onClose,
  equipmentName,
  sampleName,
  customerName,
  siteName,
  date,
  reportNumber,
  sections,
  passCount,
  warnCount,
  failCount,
  rawParams,
  regulatoryBody,
  regulatoryLegislation,
  preparedBy,
  engineerRef,
}: ValidationPrintReportProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = reportNumber
      ? `${reportNumber} — DAF Validation Report`
      : `${equipmentName} Validation Report — ${sampleName || 'No Sample'}`;
    window.print();
    document.title = originalTitle;
  };

  if (!isOpen) return null;

  const total = passCount + warnCount + failCount;
  const passPct = total > 0 ? Math.round((passCount / total) * 100) : 0;

  const paramLabels: Record<string, string> = {
    flowRate: 'Flow Rate', tss: 'TSS', og: 'O&G', bod: 'BOD', cod: 'COD',
    ph: 'pH', temp: 'Temperature', alkalinity: 'Alkalinity', turbidity: 'Turbidity',
    conductivity: 'Conductivity', hardness: 'Hardness', nh3n: 'NH₃-N', tp: 'Total P',
  };
  const paramUnits: Record<string, string> = {
    flowRate: 'm³/d', tss: 'mg/L', og: 'mg/L', bod: 'mg/L', cod: 'mg/L',
    ph: '', temp: '°C', alkalinity: 'mg/L', turbidity: 'NTU',
    conductivity: 'μS/cm', hardness: 'mg/L', nh3n: 'mg/L', tp: 'mg/L',
  };

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          @page { margin: 10mm; size: A4 portrait; }
          body * { visibility: hidden; }
          #validation-report-content, #validation-report-content * { visibility: visible; }
          #validation-report-content {
            position: absolute;
            left: 0; top: 0; width: 100%;
            background: white !important;
            color: black !important;
            font-size: 7pt !important;
          }
          .no-print   { display: none !important; }
          .screen-only { display: none !important; }
          .print-only  { display: block !important; }
          /* Never break inside a row or between the summary table rows */
          #print-summary-table tr { page-break-inside: avoid; }
        }
        @media screen {
          .print-only { display: none !important; }
        }
      `}</style>

      {/* Modal backdrop */}
      <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4">
        <div className="relative w-full max-w-[900px] bg-white rounded-xl shadow-2xl my-8">
          {/* Modal header — hidden when printing */}
          <div className="no-print flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <div className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-slate-600" />
              <h2 className="text-sm font-bold text-slate-700">Print Preview</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Report content */}
          <div
            id="validation-report-content"
            ref={printRef}
            className="p-8 bg-white text-slate-900"
          >
            {/* ══ DOCUMENT HEADER — engineering regulatory standard layout ══ */}
            <div className="border-2 border-slate-800 mb-6">

              {/* Row 1 — company block + document reference box */}
              <div className="flex items-stretch border-b border-slate-800">

                {/* Left: company identity + document title */}
                <div className="flex-1 p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-0.5">
                    Reynolds &amp; Bauhm Ltd
                  </p>
                  <p className="text-[10px] text-slate-400 mb-3">
                    Wastewater Treatment Engineering · Environmental Solutions
                  </p>
                  <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight">
                    Equipment Validation Report
                  </h1>
                  <p className="text-sm font-semibold text-slate-600 mt-0.5">
                    {equipmentName}
                  </p>
                  {regulatoryBody && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <BookOpen className="w-3 h-3 text-indigo-500 shrink-0" />
                      <p className="text-[10px] text-indigo-700 font-semibold">
                        {regulatoryBody}
                      </p>
                    </div>
                  )}
                </div>

                {/* Right: document reference box — gold on navy */}
                <div className="w-52 shrink-0 border-l border-slate-800 bg-slate-900 text-white flex flex-col divide-y divide-slate-700">
                  <div className="px-4 py-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
                      Document Reference
                    </p>
                    <p className="text-[13px] font-black font-mono tracking-wider text-rb-brand leading-tight">
                      {reportNumber || 'RB-DAF-DRAFT'}
                    </p>
                  </div>
                  <div className="px-4 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Issue Date</p>
                    <p className="text-[11px] font-semibold text-white">{date}</p>
                  </div>
                  <div className="px-4 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Status</p>
                    <p className="text-[11px] font-bold text-emerald-400">ISSUED — FINAL</p>
                  </div>
                  <div className="px-4 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Revision</p>
                    <p className="text-[11px] font-semibold text-white">Rev A</p>
                  </div>
                </div>
              </div>

              {/* Row 2 — project metadata strip */}
              <div className="grid grid-cols-4 divide-x divide-slate-800 bg-slate-50">
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Client / Project</p>
                  <p className="text-[11px] font-semibold text-slate-800 truncate">
                    {customerName || 'Reynolds & Bauhm Internal'}
                  </p>
                </div>
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Site / Location</p>
                  <p className="text-[11px] font-semibold text-slate-800 truncate">
                    {siteName || sampleName || '—'}
                  </p>
                </div>
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Basis of Design</p>
                  <p className="text-[11px] font-semibold text-slate-800 truncate">
                    {sampleName || '—'}
                  </p>
                </div>
                <div className="px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Prepared by</p>
                  <p className="text-[11px] font-semibold text-slate-800">
                    {preparedBy || 'Reynolds & Bauhm Ltd'}
                  </p>
                  {engineerRef && (
                    <p className="text-[10px] text-slate-500 mt-0.5 font-mono">{engineerRef}</p>
                  )}
                </div>
              </div>
            </div>

            {/* REGULATORY LEGISLATION — numbered list with disclaimer */}
            {regulatoryBody && regulatoryLegislation && regulatoryLegislation.length > 0 && (
              <div className="mb-6 border border-indigo-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2 bg-indigo-700">
                  <BookOpen className="w-3.5 h-3.5 text-indigo-200 shrink-0" />
                  <p className="text-[10px] text-indigo-100 uppercase font-bold tracking-wide flex-1">
                    Applicable Legislation &amp; Regulatory Framework
                  </p>
                  {reportNumber && (
                    <p className="text-[10px] text-indigo-300 font-mono">{reportNumber}</p>
                  )}
                </div>
                <div className="px-4 py-3 bg-indigo-50">
                  <div className="space-y-1 mb-2">
                    {regulatoryLegislation.map((leg, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-indigo-800">
                        <span className="text-indigo-400 font-bold shrink-0">{i + 1}.</span>
                        <span>{leg}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-indigo-500 pt-2 border-t border-indigo-200 italic">
                    Statutory compliance assessed in Step 14. Site-specific consent conditions issued
                    by {regulatoryBody} may impose stricter discharge limits — these must be satisfied
                    independently of the generic standards applied herein.
                  </p>
                </div>
              </div>
            )}

            {/* RAW PARAMETERS */}
            {rawParams && Object.keys(rawParams).length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-bold text-slate-500 uppercase mb-3 border-b border-slate-200 pb-1">
                  Raw Influent Parameters
                </h3>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2 text-xs">
                  {Object.entries(rawParams).map(([key, val]) => (
                    <div key={key} className="bg-slate-50 rounded p-2 border border-slate-100">
                      <span className="text-slate-400 block">{paramLabels[key] || key}</span>
                      <span className="font-bold text-slate-800">
                        {typeof val === 'number' ? val.toFixed(key === 'ph' || key === 'tp' ? 1 : 0) : val} {paramUnits[key]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SUMMARY BAR */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 text-center">
                <p className="text-[10px] text-emerald-600 uppercase font-bold">Passed</p>
                <p className="text-2xl font-bold text-emerald-700">{passCount}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 text-center">
                <p className="text-[10px] text-amber-600 uppercase font-bold">Warnings</p>
                <p className="text-2xl font-bold text-amber-700">{warnCount}</p>
              </div>
              <div className="bg-rose-50 rounded-lg p-3 border border-rose-200 text-center">
                <p className="text-[10px] text-rose-600 uppercase font-bold">Failures</p>
                <p className="text-2xl font-bold text-rose-700">{failCount}</p>
              </div>
            </div>
            <div className="mb-8">
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex">
                {total > 0 && (
                  <>
                    <div className="h-full bg-emerald-500" style={{ width: `${(passCount / total) * 100}%` }} />
                    <div className="h-full bg-amber-500" style={{ width: `${(warnCount / total) * 100}%` }} />
                    <div className="h-full bg-rose-500" style={{ width: `${(failCount / total) * 100}%` }} />
                  </>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1 text-center">
                {passPct}% of {total} validation checks passed
              </p>
            </div>

            {/* ══ PRINT-ONLY: compact single-page master summary table ══ */}
            <div className="print-only">
              <h3 style={{ fontSize: '8pt', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--rb-text-4)', borderBottom: '1px solid #e2e8f0', paddingBottom: '3px', marginBottom: '6px' }}>
                Validation Checks — All Sections Summary
              </h3>
              <table id="print-summary-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7pt' }}>
                <thead>
                  <tr style={{ background: '#1e293b', color: 'white' }}>
                    <th style={{ textAlign: 'left', padding: '3px 4px', width: '22%', fontWeight: 'bold' }}>Section</th>
                    <th style={{ textAlign: 'left', padding: '3px 4px', width: '28%', fontWeight: 'bold' }}>Check / Parameter</th>
                    <th style={{ textAlign: 'left', padding: '3px 4px', width: '22%', fontWeight: 'bold', fontFamily: 'monospace', fontSize: '6.5pt' }}>Expression</th>
                    <th style={{ textAlign: 'right', padding: '3px 4px', width: '15%', fontWeight: 'bold' }}>Result</th>
                    <th style={{ textAlign: 'center', padding: '3px 4px', width: '13%', fontWeight: 'bold' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.flatMap((section, si) =>
                    section.equations.map((eq, ei) => {
                      const bg = ei % 2 === 0 ? 'var(--rb-text)' : 'white';
                      const statusColor = eq.status === 'pass' ? '#15803d' : eq.status === 'warn' ? '#b45309' : '#b91c1c';
                      const statusBg   = eq.status === 'pass' ? '#dcfce7' : eq.status === 'warn' ? '#fef3c7' : '#fee2e2';
                      return (
                        <tr key={`${si}-${ei}`} style={{ background: bg, borderBottom: '1px solid #e2e8f0' }}>
                          {ei === 0 && (
                            <td rowSpan={section.equations.length} style={{ padding: '3px 4px', fontWeight: 'bold', color: '#1e293b', borderRight: '2px solid #cbd5e1', verticalAlign: 'top', fontSize: '6.5pt' }}>
                              {si + 1}. {section.title}
                            </td>
                          )}
                          <td style={{ padding: '3px 4px', fontWeight: 600, color: '#334155' }}>{eq.label}</td>
                          <td style={{ padding: '3px 4px', fontFamily: 'monospace', fontSize: '6pt', color: 'var(--rb-text-3)' }}>{eq.substitution || eq.formula || '—'}</td>
                          <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace', color: 'var(--rb-elevated)' }}>{eq.result} {eq.unit}</td>
                          <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                            <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: '999px', background: statusBg, color: statusColor, fontWeight: 'bold', fontSize: '6.5pt', border: `1px solid ${statusColor}33` }}>
                              {eq.status.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {/* Any warnings */}
              {sections.flatMap(s => s.warnings || []).length > 0 && (
                <div style={{ marginTop: '6px', padding: '4px 6px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '4px' }}>
                  <p style={{ fontWeight: 'bold', fontSize: '7pt', color: '#9a3412', marginBottom: '2px' }}>Design Warnings</p>
                  {sections.flatMap((s, si) => (s.warnings || []).map((w, wi) => (
                    <p key={`${si}-${wi}`} style={{ fontSize: '6.5pt', color: '#c2410c' }}>⚠ [{s.title}] {w}</p>
                  )))}
                </div>
              )}
            </div>

            {/* ══ SCREEN-ONLY: full per-section detail tables ══ */}
            <div className="screen-only">
              {sections.map((section, si) => {
                const Icon = section.icon && iconMap[section.icon.name] ? iconMap[section.icon.name] : FlaskConical;
                return (
                  <div key={si}>
                    <div className="flex items-center gap-2 mb-3 mt-6">
                      <Icon className="w-4 h-4 text-slate-600" />
                      <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                        {si + 1}. {section.title}
                      </h2>
                    </div>

                    <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse mb-4" style={{ minWidth: '560px' }}>
                      <thead>
                        <tr className="bg-slate-100 border-b border-slate-300">
                          <th className="text-left p-2 font-bold text-slate-600 w-[30%]">Parameter</th>
                          <th className="text-left p-2 font-bold text-slate-600 w-[25%]">Formula</th>
                          <th className="text-left p-2 font-bold text-slate-600 w-[25%]">Substitution</th>
                          <th className="text-right p-2 font-bold text-slate-600 w-[12%]">Result</th>
                          <th className="text-center p-2 font-bold text-slate-600 w-[8%]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.equations.map((eq, ei) => (
                          <tr key={ei} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-2 font-semibold text-slate-800">{eq.label}</td>
                            <td className="p-2 font-mono text-[10px] text-slate-500">{eq.formula}</td>
                            <td className="p-2 font-mono text-[10px] text-slate-600">{eq.substitution}</td>
                            <td className="p-2 text-right font-bold text-slate-800">
                              {eq.result} {eq.unit}
                            </td>
                            <td className="p-2 text-center">
                              <StatusBadge status={eq.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>

                    {section.warnings && section.warnings.length > 0 && (
                      <div className="mb-4 space-y-1">
                        {section.warnings.map((w, wi) => (
                          <div key={wi} className="text-xs p-2 rounded bg-rose-50 border border-rose-200 text-rose-700">
                            ⚠️ {w}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* FOOTER */}
            <div className="mt-10 border-t-2 border-slate-800 pt-3">
              {/* Primary footer strip — report ref + company */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                    Reynolds &amp; Bauhm Ltd · Wastewater Treatment Engineering
                  </p>
                  {preparedBy && (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Prepared by: <strong>{preparedBy}</strong>
                      {engineerRef ? ` · Ref: ${engineerRef}` : ''}
                    </p>
                  )}
                </div>
                {reportNumber && (
                  <p className="text-[10px] font-black font-mono text-rb-brand tracking-wider">
                    {reportNumber}
                  </p>
                )}
              </div>
              {/* Regulatory compliance disclaimer */}
              <div className="border-t border-slate-200 pt-2 space-y-1">
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  <strong>Confidential.</strong> This document has been prepared solely for{' '}
                  {customerName || 'the named client'} and must not be reproduced or distributed
                  to third parties without the written consent of Reynolds &amp; Bauhm Ltd.
                </p>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  All calculations are based on the influent characterisation data provided and
                  recognised engineering standards (Metcalf &amp; Eddy, WEF MOP-8, EN 12255).
                  {regulatoryBody
                    ? ` Statutory limits applied from ${regulatoryBody}. Site-specific consent conditions issued under the applicable permit must also be met.`
                    : ' Site-specific consent conditions must be independently verified.'}
                </p>
                <p className="text-[10px] text-slate-400">
                  Document Ref: {reportNumber || 'RB-DAF-DRAFT'} · Issue Date: {date} · Rev A · Status: ISSUED — FINAL
                  {preparedBy ? ` · Prepared by: ${preparedBy}` : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
